
import { jsPDF } from "jspdf";
import { GalleryPhoto } from "@/components/gallery/carousel/types";
import { PdfExportOptions, PhotosByWeek } from "../types";
import { 
  addImagePlaceholder, 
  calculateLayoutConfig, 
  addPageNumbers,
  createSafeFileName,
  addPdfTitle,
  addDecorativeLine
} from "../utils/pdfUtils";
import { prepareImageForPdf } from "../utils/imageUtils";

/**
 * Generates a pregnancy gallery PDF organized by weeks
 */
export const generatePregnancyGalleryPdf = async (
  photos: GalleryPhoto[],
  collectionTitle: string | undefined,
  options: PdfExportOptions
): Promise<string> => {
  // Create a new PDF document
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });
  
  // Calculate layout dimensions
  const layout = calculateLayoutConfig(doc);
  
  // Add title
  const title = collectionTitle || 'Pregnancy Collection';
  addPdfTitle(doc, title);
  
  // Add decorative line
  if (options.includeDecorations) {
    addDecorativeLine(doc);
  }
  
  // Start position for photos
  let yPosition = 40;
  
  // Group photos by pregnancy week
  const photosByWeek = groupPhotosByPregnancyWeek(photos);
  
  // Sort weeks naturally (Week 1, Week 2, ... Week 10 instead of Week 1, Week 10, Week 2)
  const sortedWeeks = sortWeeksNaturally(photosByWeek);
  
  // Process photos by week
  for (const week of sortedWeeks) {
    // Add week header
    if (yPosition + 10 > doc.internal.pageSize.getHeight()) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFontSize(14);
    doc.setTextColor(44, 62, 80);
    doc.text(`${week}`, layout.margin, yPosition);
    
    if (options.includeDecorations) {
      doc.setDrawColor(231, 76, 60);
      doc.setLineWidth(0.3);
      doc.line(layout.margin, yPosition + 2, layout.margin + 40, yPosition + 2);
    }
    
    yPosition += 10;
    
    // Process photos for this week
    const weekPhotos = photosByWeek[week];
    let xCounter = 0;
    
    for (let i = 0; i < weekPhotos.length; i++) {
      // Start a new page if needed
      if (yPosition + layout.imageHeight + 10 > doc.internal.pageSize.getHeight()) {
        doc.addPage();
        yPosition = 20;
      }
      
      const photo = weekPhotos[i];
      const xPosition = layout.margin + (xCounter % layout.imagesPerRow) * layout.imageWidth;
      
      // Process image and add to PDF
      try {
        // Use enhanced image loading
        const imgData = await prepareImageForPdf(photo.url);
        
        if (imgData) {
          // Add image to PDF
          doc.addImage({
            imageData: imgData, 
            format: 'JPEG', 
            x: xPosition, 
            y: yPosition, 
            width: layout.imageWidth - 5, 
            height: layout.imageHeight,
            compression: 'FAST'
          });
          
          // Add photo title - using standard safe encoding
          doc.setFontSize(10);
          doc.setTextColor(44, 62, 80);
          // Use a safe position for the text to avoid overflow
          const titleX = xPosition + (layout.imageWidth - 5) / 2;
          const titleY = yPosition + layout.imageHeight + 5;
          // Limit title length
          const safeTitle = photo.title.substring(0, 30);
          doc.text(safeTitle, titleX, titleY, { align: 'center' });
          
          // Add photo date if requested
          if (options.includePhotoDates) {
            const createDate = photo.created_at ? new Date(photo.created_at).toLocaleDateString() : new Date().toLocaleDateString();
            doc.setFontSize(7);
            doc.setTextColor(127, 140, 141);
            doc.text(createDate, xPosition, yPosition + layout.imageHeight + 12);
          }
        } else {
          // Use placeholder for failed image
          addImagePlaceholder(doc, xPosition, yPosition, layout.imageWidth - 5, layout.imageHeight, photo.title);
        }
        
        // Move position counter
        xCounter++;
        
        // Move to next row if needed
        if (xCounter % layout.imagesPerRow === 0) {
          yPosition += layout.imageHeight + layout.spacing;
        }
      } catch (error) {
        console.error(`Error adding image ${photo.url} to PDF:`, error);
        // Add placeholder for failed image
        addImagePlaceholder(doc, xPosition, yPosition, layout.imageWidth - 5, layout.imageHeight, photo.title);
        
        xCounter++;
        
        // Move to next row if needed
        if (xCounter % layout.imagesPerRow === 0) {
          yPosition += layout.imageHeight + layout.spacing;
        }
      }
    }
    
    // Make sure we're at a new row for the next week
    if (xCounter % layout.imagesPerRow !== 0) {
      yPosition += layout.imageHeight + layout.spacing;
    }
    
    // Add extra spacing between weeks
    yPosition += 5;
  }
  
  // Add page numbers
  addPageNumbers(doc);
  
  // Create safe filename and save PDF
  const sanitizedTitle = createSafeFileName(collectionTitle);
  const fileName = `${sanitizedTitle}_photos.pdf`;
  doc.save(fileName);
  
  return fileName;
};

/**
 * Group photos by pregnancy week
 */
function groupPhotosByPregnancyWeek(photos: GalleryPhoto[]): PhotosByWeek {
  const photosByWeek: PhotosByWeek = {};
  
  photos.forEach(photo => {
    const category = photo.category || '';
    const weekMatch = category.match(/pregnancy-week-(\d+)/);
    const week = weekMatch ? `Week ${weekMatch[1]}` : category.replace('pregnancy-', '').replace('-', ' ');
    
    if (!photosByWeek[week]) {
      photosByWeek[week] = [];
    }
    photosByWeek[week].push(photo);
  });
  
  return photosByWeek;
}

/**
 * Sort weeks naturally (Week 1, Week 2, ... Week 10 instead of Week 1, Week 10, Week 2)
 */
function sortWeeksNaturally(photosByWeek: PhotosByWeek): string[] {
  return Object.keys(photosByWeek).sort((a, b) => {
    const aNum = parseInt(a.replace('Week ', ''));
    const bNum = parseInt(b.replace('Week ', ''));
    
    // If both are valid numbers, sort numerically
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }
    
    // If not both numbers, sort alphabetically
    return a.localeCompare(b);
  });
}
